import { EventEmitter } from 'events';
import BluetoothWrapper, { weightEmitter } from './BluetoothWrapper';
import * as sensorDataAnalysis from './sensorDataAnalysis';
import { std, mean } from 'mathjs';

interface CalibrationData {
  samples: number[];
  userWeight: number;
  addedWeights: number;
  isCalibrated: boolean;
}

interface GlobalBluetoothWrapperCallbacks {
  onConnectionChange?: (connected: boolean, deviceName?: string, deviceId?: string) => void;
  onCalibrationChange?: (calibrated: boolean, weight?: number, addedWeights?: number) => void;
  onCalibrationProgress?: (sampleCount: number, totalSamples: number) => void;
  onDataReceived?: (data: any) => void;
  onError?: (error: string) => void;
}

class GlobalBluetoothWrapper {
  private static instance: GlobalBluetoothWrapper;
  private bluetoothWrapper: BluetoothWrapper;
  private eventEmitter: EventEmitter;
  
  // Connection state
  private isConnected: boolean = false;
  private isConnecting: boolean = false;
  private deviceName: string | null = null;
  private deviceId: string | null = null;
  private connectionError: string | null = null;
  
  // Calibration state
  private isCalibrated: boolean = false;
  private isCalibrating: boolean = false;
  private userWeight: number = 0;
  private addedWeights: number = 0;
  private calibrationError: string | null = null;
  private calibrationData: CalibrationData = {
    samples: [],
    userWeight: 0,
    addedWeights: 0,
    isCalibrated: false,
  };
  
  // Callbacks
  private callbackSets: Set<GlobalBluetoothWrapperCallbacks> = new Set();
  
  // Data stream state
  private isDataStreamActive: boolean = false;
  private dataCallbacks: Set<(data: any) => void> = new Set();

  private constructor() {
    this.eventEmitter = new EventEmitter();
    this.bluetoothWrapper = new BluetoothWrapper((data: any) => {
      // console.log('GlobalBluetoothWrapper: Raw data received from BluetoothWrapper:', data);
      this.onBluetoothDataReceived(data);
    });
    
    // Listen for disconnection events from BluetoothWrapper
    weightEmitter.on('bluetoothDisconnected', (data: any) => {
      this.handleDisconnection(data);
    });
    
  }

  static getInstance(): GlobalBluetoothWrapper {
    if (!GlobalBluetoothWrapper.instance) {
      GlobalBluetoothWrapper.instance = new GlobalBluetoothWrapper();
    }
    return GlobalBluetoothWrapper.instance;
  }

  // Connection methods
  async connect(): Promise<void> {
    if (this.isConnecting) {
      return;
    }

    // Check if already connected by checking both internal state and underlying BluetoothWrapper
    const isUnderlyingConnected = this.bluetoothWrapper.isConnected();
    if (this.isConnected && isUnderlyingConnected) {
      return;
    }

    // If internal state says connected but underlying wrapper doesn't, sync the state
    if (this.isConnected && !isUnderlyingConnected) {
      this.isConnected = false;
      this.deviceName = null;
      this.deviceId = null;
      this.connectionError = 'Connection lost';
      this.notifyConnectionChange();
    }

    if (!this.isConnected && isUnderlyingConnected) {
      this.isConnected = true;
      this.deviceName = 'Entralpi Device'; // TODO: Get actual device name
      this.deviceId = 'device-123'; // TODO: Get actual device ID
      this.connectionError = null;
      this.notifyConnectionChange();
      
      // Start data stream subscription
      try {
        await this.bluetoothWrapper.addNotifications();
      } catch (error) {
        console.warn('GlobalBluetoothWrapper: Failed to start data stream subscription:', error);
      }
      return;
    }

    this.isConnecting = true;
    this.connectionError = null;
    this.notifyConnectionChange();

    try {
      await this.bluetoothWrapper.init();
      
      if (this.bluetoothWrapper.isConnected()) {
        this.isConnected = true;
        this.deviceName = 'Entralpi Device'; // TODO: Get actual device name
        this.deviceId = 'device-123'; // TODO: Get actual device ID
        
        // Automatically start data stream subscription when connected
        try {
          await this.bluetoothWrapper.addNotifications();
        } catch (error) {
          console.warn('GlobalBluetoothWrapper: Failed to start data stream subscription:', error);
        }
      } else {
        this.connectionError = 'Auto-connect failed. Please connect manually.';
      }
    } catch (error) {
      console.error('GlobalBluetoothWrapper: Connection failed:', error);
      this.connectionError = error.message || 'Connection failed';
      this.isConnected = false;
    } finally {
      this.isConnecting = false;
      this.notifyConnectionChange();
    }
  }

  async disconnect(): Promise<void> {
    if (!this.isConnected) {
      return;
    }

    try {
      this.bluetoothWrapper.disconnect();
      this.isConnected = false;
      this.deviceName = null;
      this.deviceId = null;
      this.connectionError = null;
      
      // Reset calibration when disconnecting
      this.resetCalibration();
      
      this.notifyConnectionChange();
    } catch (error) {
      console.error('GlobalBluetoothWrapper: Disconnect error:', error);
      this.connectionError = error.message || 'Disconnect failed';
      this.notifyError(this.connectionError);
    }
  }

  // Handle unexpected disconnection (e.g., device turned off, out of range)
  private handleDisconnection(data: any): void {
    // Update connection state
    this.isConnected = false;
    this.deviceName = null;
    this.deviceId = null;
    this.connectionError = data.reason || 'Device disconnected unexpectedly';
    
    // Reset calibration and data stream
    this.resetCalibration();
    this.isDataStreamActive = false;
    this.dataCallbacks.clear();
    
    // Notify all listeners about the disconnection
    this.notifyConnectionChange();
    
    // Emit disconnection event for other components
    this.eventEmitter.emit('disconnected', {
      reason: data.reason,
      timestamp: data.timestamp
    });
  }

  // Check if the device is actually still connected
  async checkConnectionStatus(): Promise<boolean> {
    if (!this.isConnected) {
      return false;
    }

    try {
      // For WebBluetooth, we can check if the device is still connected
      // by trying to access the GATT server
      if (this.bluetoothWrapper.devicePlatForm === 'web') {
        // This is a simplified check - in a real implementation, you might
        // want to try to read a characteristic or check the GATT server state
        return this.bluetoothWrapper.isConnected();
      }
      
      return this.isConnected;
    } catch (error) {
      // If we can't check the connection, assume it's disconnected
      this.handleDisconnection({
        reason: 'Connection check failed',
        timestamp: Date.now()
      });
      return false;
    }
  }

  // Sync connection state with underlying BluetoothWrapper
  async syncConnectionState(): Promise<void> {
    const underlyingConnected = this.bluetoothWrapper.isConnected();
    const internalConnected = this.isConnected;
    
    if (underlyingConnected && !internalConnected) {
      // Underlying wrapper is connected but internal state isn't - sync up
      this.isConnected = true;
      this.deviceName = 'Entralpi Device'; // TODO: Get actual device name
      this.deviceId = 'device-123'; // TODO: Get actual device ID
      this.connectionError = null;
      this.notifyConnectionChange();
    } else if (!underlyingConnected && internalConnected) {
      // Internal state says connected but underlying wrapper doesn't - sync down
      this.isConnected = false;
      this.deviceName = null;
      this.deviceId = null;
      this.connectionError = 'Connection lost';
      this.notifyConnectionChange();
    }
  }

  // Calibration methods
  startCalibration(): void {
    if (!this.isConnected) {
      this.calibrationError = 'Device not connected';
      this.notifyCalibrationChange();
      return;
    }

    if (this.isCalibrating) {
      return;
    }

    this.isCalibrating = true;
    this.calibrationError = null;
    this.calibrationData = {
      samples: [],
      userWeight: 0,
      addedWeights: 0,
      isCalibrated: false,
    };
    
    // Start data stream for calibration
    this.startDataStream((data) => {
      this.processCalibrationData(data);
    });
    
    this.notifyCalibrationChange();
  }

  processCalibrationData(data: number): void {
    // If not calibrating, this data should be processed as regular sensor data
    if (!this.isCalibrating) {
      // This is normal - data stream continues after calibration for exercise
      // But we should NOT call this.dataCallback here as it would create infinite recursion
      // The data stream should be managed by the exercise components after calibration
      return;
    }

    // Validate data quality
    if (data < 0 || data > 1000) {
      console.warn('GlobalBluetoothWrapper: Invalid calibration data:', data);
      return;
    }

    // Check for outliers if we have previous samples
    if (this.calibrationData.samples.length > 0) {
      const previousMean = this.calibrationData.samples.reduce((a, b) => a + b, 0) / this.calibrationData.samples.length;
      const deviation = Math.abs(data - previousMean);
      
      // If deviation is more than 50kg from previous mean, it's likely an outlier
      if (deviation > 50) {
        console.warn('GlobalBluetoothWrapper: Potential outlier detected:', data, 'deviation:', deviation.toFixed(1), 'kg');
        return;
      }
    }

    this.calibrationData.samples.push(data);

    this.callbackSets.forEach(callbacks => {
      if (callbacks.onCalibrationProgress) {
        callbacks.onCalibrationProgress(this.calibrationData.samples.length, 10);
      }
    });

    if (this.calibrationData.samples.length >= 10) {
      this.finishCalibration();
    }
  }

  private finishCalibration(): void {
    if (this.calibrationData.samples.length < 10) {
      this.calibrationError = 'Not enough samples for calibration. Please try again.';
      this.isCalibrating = false;
      this.stopDataStream();
      this.notifyCalibrationChange();
      return;
    }

    const samples = this.calibrationData.samples;
    const meanValue = mean(samples);
    const stdDev = std(samples);

    // Validate calibration
    if (meanValue < 1 || meanValue > 200) {
      this.calibrationError = `Weight out of range (${meanValue.toFixed(1)}kg). Please ensure the device is properly loaded and try again.`;
      this.isCalibrating = false;
      this.stopDataStream();
      this.notifyCalibrationChange();
      return;
    }

    if (stdDev > 0.5) {
      this.calibrationError = `Weight readings are unstable (variation: ${stdDev.toFixed(1)}kg). Please ensure the device is stable, not moving, and try again.`;
      this.isCalibrating = false;
      this.stopDataStream();
      this.notifyCalibrationChange();
      return;
    }

    // Calibration successful
    this.userWeight = Math.round(meanValue * 10) / 10; // Round to 1 decimal
    this.addedWeights = 0; // Default to no added weights - this represents the user's body weight
    this.isCalibrated = true;
    this.isCalibrating = false;
    this.calibrationError = null;

    // Stop the calibration data stream to prevent infinite recursion
    // The exercise components will start their own data stream
    this.stopDataStream();
    
    this.notifyCalibrationChange();
  }

  resetCalibration(): void {
    this.isCalibrated = false;
    this.isCalibrating = false;
    this.userWeight = 0;
    this.addedWeights = 0;
    this.calibrationError = null;
    this.calibrationData = {
      samples: [],
      userWeight: 0,
      addedWeights: 0,
      isCalibrated: false,
    };
    this.notifyCalibrationChange();
  }

  updateCalibrationData(userWeight: number, addedWeights: number): void {
    this.userWeight = userWeight;
    this.addedWeights = addedWeights;
    this.calibrationData.userWeight = userWeight;
    this.calibrationData.addedWeights = addedWeights;
    this.notifyCalibrationChange();
  }

  setAddedWeights(addedWeights: number): void {
    this.addedWeights = addedWeights;
    this.notifyCalibrationChange();
  }

  // Data stream methods
  /**
   * Add a data callback. Returns an unsubscribe function.
   * Multiple subscribers can coexist; all receive every BLE sample.
   */
  addDataCallback(callback: (data: any) => void): () => void {
    this.dataCallbacks.add(callback);
    return () => this.dataCallbacks.delete(callback);
  }

  startDataStream(callback: (data: any) => void): void {
    if (!this.isConnected) {
      console.error('GlobalBluetoothWrapper: Cannot start data stream - not connected');
      return;
    }

    this.dataCallbacks.add(callback);
    this.isDataStreamActive = true;

    // Start notifications in the underlying BluetoothWrapper
    this.bluetoothWrapper.addNotifications().then(() => {
    }).catch((error) => {
      console.error('GlobalBluetoothWrapper: Failed to start notifications:', error);
      this.isDataStreamActive = false;
    });
  }

  stopDataStream(): void {
    this.dataCallbacks.clear();
    this.isDataStreamActive = false;

    // Stop data processing but keep notifications active
    if (this.bluetoothWrapper) {
      this.bluetoothWrapper.removeStream();
    }
  }

  // Method to ensure data stream is active and register a callback
  async ensureDataStreamActive(callback: (data: any) => void): Promise<void> {
    if (!this.isConnected) {
      console.warn('GlobalBluetoothWrapper: Cannot ensure data stream - not connected');
      return;
    }

    // Don't interfere with calibration data stream
    if (this.isCalibrating) {
      return;
    }

    this.dataCallbacks.add(callback);

    if (!this.isDataStreamActive) {
      try {
        await this.bluetoothWrapper.addNotifications();
        this.isDataStreamActive = true;
      } catch (error) {
        console.error('GlobalBluetoothWrapper: Failed to start data stream:', error);
        this.dataCallbacks.delete(callback);
        throw error;
      }
    }
  }

  private onBluetoothDataReceived(data: any): void {
    // console.log('GlobalBluetoothWrapper: Received data:', data, 'isCalibrating:', this.isCalibrating, 'isDataStreamActive:', this.isDataStreamActive);
    
    // Handle calibration data
    if (this.isCalibrating) {
      const numericData = parseFloat(data);
      if (!isNaN(numericData)) {
        this.processCalibrationData(numericData);
      }
      return;
    }

    // Handle workout data — fan out to all subscribers
    if (this.isDataStreamActive && this.dataCallbacks.size > 0) {
      this.dataCallbacks.forEach(cb => {
        try { cb(data); } catch (e) { console.error('GlobalBluetoothWrapper: callback error', e); }
      });
    }
  }

  // Callback management
  setCallbacks(callbacks: GlobalBluetoothWrapperCallbacks): () => void {
    this.callbackSets.add(callbacks);
    return () => this.callbackSets.delete(callbacks);
  }

  private notifyConnectionChange(): void {
    this.callbackSets.forEach(callbacks => {
      if (callbacks.onConnectionChange) {
        callbacks.onConnectionChange(this.isConnected, this.deviceName, this.deviceId);
      }
    });
  }

  private notifyCalibrationChange(): void {
    this.callbackSets.forEach(callbacks => {
      if (callbacks.onCalibrationChange) {
        callbacks.onCalibrationChange(this.isCalibrated, this.userWeight, this.addedWeights);
      }
    });
  }

  private notifyError(error: string): void {
    this.callbackSets.forEach(callbacks => {
      if (callbacks.onError) {
        callbacks.onError(error);
      }
    });
  }

  // Getters
  getConnectionState() {
    return {
      isConnected: this.isConnected,
      isConnecting: this.isConnecting,
      deviceName: this.deviceName,
      deviceId: this.deviceId,
      error: this.connectionError,
    };
  }

  getCalibrationState() {
    return {
      isCalibrated: this.isCalibrated,
      isCalibrating: this.isCalibrating,
      userWeight: this.userWeight,
      addedWeights: this.addedWeights,
      error: this.calibrationError,
      sampleCount: this.calibrationData.samples.length,
      totalSamples: 10,
    };
  }

  isDataStreamRunning(): boolean {
    return this.isDataStreamActive;
  }

  // Check if setup is complete
  isSetupComplete(): boolean {
    return this.isConnected && this.isCalibrated;
  }

  // Get setup status
  getSetupStatus(): 'not_connected' | 'connected_not_calibrated' | 'ready' {
    if (!this.isConnected) return 'not_connected';
    if (!this.isCalibrated) return 'connected_not_calibrated';
    return 'ready';
  }
}

export default GlobalBluetoothWrapper;
